import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from './api';

const PAGE_SIZE = 30;
const date = value => value ? new Date(value).toLocaleString('tr-TR') : 'Henüz yok';
const labels = {
  goal: 'Hedef', challenge: 'Zorlandığı durum', intention: 'Niyet', name: 'İsim',
  dietaryPreference: 'Beslenme tercihi', dietary_preference: 'Beslenme tercihi',
  avoidedFoods: 'Kaçınılan besinler', avoided_foods: 'Kaçınılan besinler',
  age: 'Yaş', sex: 'Cinsiyet', heightCM: 'Boy (cm)', weightKG: 'Kilo (kg)',
  activityLevel: 'Aktivite düzeyi', memoryEnabled: 'Hafıza kullanımı',
  reminderEnabled: 'Hatırlatıcılar', ingredients: 'İçindekiler', category: 'Öğün',
  calorie_lower: 'Alt kalori tahmini', calorie_upper: 'Üst kalori tahmini',
  calorieLower: 'Alt kalori tahmini', calorieUpper: 'Üst kalori tahmini',
};
const choiceLabels = {
  goal: { regularMeals: 'Daha düzenli yemek', awareness: 'Yediklerini fark etmek', weightManagement: 'Kilo yönetimi', support: 'Beslenme konusunda destek' },
  challenge: { skippedMeals: 'Yoğunlukta öğünleri atlıyor', deciding: 'Yemek seçmekte zorlanıyor', consistency: 'Düzenini sürdürmekte zorlanıyor', exploring: 'Şimdilik keşfediyor' },
  dietaryPreference: { none: 'Özel bir tercihi yok', vegetarian: 'Vejetaryen', vegan: 'Vegan' },
};
const memoryCategories = { preference: 'Tercih', constraint: 'Kısıt', routine: 'Günlük düzen', goal: 'Hedef', strategy: 'İşe yarayan yöntem' };
const memoryStates = { pending: 'Onay bekliyor', confirmed: 'Doğrulandı', rejected: 'Reddedildi', deleted: 'Unutuldu' };
const habitStates = { proposed: 'Önerildi', active: 'Aktif', paused: 'Ara verildi', completed: 'Tamamlandı', dismissed: 'Tercih edilmedi' };
const outcomes = { done: 'Adımı tamamladı', not_tried: 'Henüz denemedi', did_not_fit: 'Denedi, kendisine uymadı' };
const helpfulnessLabels = { helpful: 'Yararlı buldu', neutral: 'Etkisi belirsiz', unhelpful: 'Yararlı bulmadı' };
const numberWithUnit = (value, unit) => typeof value === 'number' && Number.isFinite(value) && value >= 0
  ? `${value.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} ${unit}` : 'Belirtilmemiş';

const physicalKeys = new Set(['age', 'sex', 'heightCM', 'weightKG', 'activityLevel', 'heightUnit', 'weightUnit']);
const activities = {
  seated: 'Çoğunlukla oturarak', walking: 'Gün içinde sık sık yürüyor',
  standing: 'Çoğu zaman ayakta', physicalWork: 'Fiziksel olarak yoğun çalışıyor',
};
const measurement = (value, unit) => typeof value === 'number' && Number.isFinite(value) && value > 0
  ? `${value.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} ${unit}` : 'Belirtilmemiş';

function PhysicalProfile({ preferences }) {
  return <section className="panel"><h2>Vücut ölçüleri ve hareket</h2>
    <dl className="user-fields user-measurements">
      {[
        ['Boy', measurement(preferences.heightCM, 'cm')],
        ['Kilo', measurement(preferences.weightKG, 'kg')],
        ['Yaş', measurement(preferences.age, 'yaş')],
        ['Cinsiyet', ({ female: 'Kadın', male: 'Erkek', undisclosed: 'Paylaşmak istemiyor' })[preferences.sex] || 'Belirtilmemiş'],
        ['Günlük hareket', activities[preferences.activityLevel] || 'Belirtilmemiş'],
      ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl>
    <p className="user-data-note">Kullanıcının paylaştığı son profil değerleri. Günlük hareket düzeyi, egzersiz geçmişi anlamına gelmez.</p>
  </section>;
}

function ProfilePreferences({ preferences }) {
  const remaining = Object.fromEntries(Object.entries(preferences).filter(([key]) => !physicalKeys.has(key)));
  return <section className="panel"><h2>Profil ve tercihler</h2>{Object.keys(remaining).length
    ? <Fields value={remaining} /> : <p>Hesaba kaydedilmiş profil tercihi yok.</p>}</section>;
}

function useResource(path) {
  const [result, setResult] = useState({});
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setResult({ path });
    api(path, { signal: controller.signal }).then(data => {
      if (!controller.signal.aborted) setResult({ path, data });
    }).catch(error => {
      if (!controller.signal.aborted) setResult({ path, error: error.message });
    });
    return () => controller.abort();
  }, [path, revision]);
  return { ...(result.path === path ? result : {}), retry: () => setRevision(v => v + 1) };
}

function Result({ resource, children }) {
  if (resource.error) return <div className="panel error" role="alert">
    <p>{resource.error}</p><button onClick={resource.retry}>Yeniden dene</button>
  </div>;
  if (!resource.data) return <p className="panel muted" role="status">Yükleniyor…</p>;
  return children(resource.data);
}

function Fields({ value, field }) {
  if (value === null || value === undefined || value === '') return <span className="muted">Belirtilmemiş</span>;
  if (typeof value === 'boolean') return value ? 'Açık' : 'Kapalı';
  if (Array.isArray(value)) return value.length
    ? <ul>{value.map((item, i) => <li key={i}><Fields value={item} /></li>)}</ul>
    : <span className="muted">Kayıt yok</span>;
  if (typeof value === 'object') return <dl className="user-fields">{Object.entries(value).map(([key, item]) =>
    <div key={key}><dt>{labels[key] || key}</dt><dd><Fields value={item} field={key} /></dd></div>)}</dl>;
  return choiceLabels[field]?.[value] || String(value);
}

function Pagination({ offset, count, total = null, hasMore, onChange }) {
  if (!count && !offset) return null;
  return <div className="user-pagination" aria-label="Sayfalama">
    <button className="secondary" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - PAGE_SIZE))}>Önceki</button>
    <span>{count ? `${offset + 1}–${offset + count}${total !== null ? ` / ${total}` : ''}` : 'Kayıt yok'}</span>
    <button className="secondary" disabled={hasMore !== undefined ? !hasMore : offset + count >= total} onClick={() => onChange(offset + PAGE_SIZE)}>Sonraki</button>
  </div>;
}

export function UsersPage() {
  const [params, setParams] = useSearchParams();
  const search = params.get('search') || '';
  const rawOffset = Number(params.get('offset') || 0);
  const offset = Number.isSafeInteger(rawOffset) && rawOffset >= 0 ? rawOffset : 0;
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  const q = useResource(`/admin/users?limit=${PAGE_SIZE + 1}&offset=${offset}&search=${encodeURIComponent(search)}`);
  return <>
    <header className="page-title"><div><h1>Kullanıcılar</h1><p>Bir hesabın bilgilerini, hafızasını ve sohbetlerini incele.</p></div></header>
    <form className="user-search" onSubmit={e => { e.preventDefault(); setParams(draft.trim() ? { search: draft.trim() } : {}); }}>
      <label htmlFor="user-search">İsim, e-posta veya kullanıcı kimliği<input id="user-search" value={draft} onChange={e => setDraft(e.target.value)} type="search" /></label>
      <button type="submit">Ara</button>
    </form>
    <Result resource={q}>{data => <>
      <section className="panel table"><div className="tr users head"><span>Kullanıcı</span><span>Rol</span><span>Durum</span><span>Kayıt</span><span>İşlemler</span></div>
        {data.slice(0, PAGE_SIZE).map(user => <UserRow key={user.id} user={user} refresh={q.retry} />)}
        {!data.length && <p className="empty">Bu arama için kullanıcı bulunamadı.</p>}
      </section>
      <Pagination offset={offset} count={Math.min(data.length, PAGE_SIZE)} hasMore={data.length > PAGE_SIZE} onChange={next => setParams({ ...(search ? { search } : {}), offset: String(next) })} />
    </>}</Result>
  </>;
}

function UserRow({ user, refresh }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function toggle() {
    setBusy(true); setError('');
    try { await api(`/admin/users/${encodeURIComponent(user.id)}`, { method: 'PATCH', body: JSON.stringify({ is_active: !user.is_active }) }); refresh(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  return <div className="tr users">
    <span><Link className="user-link" to={`/users/${encodeURIComponent(user.id)}`}><b>{user.display_name}</b></Link><small>{user.email}</small></span>
    <span>{user.is_admin ? 'Studio admin' : 'Üye'}</span>
    <span className={`dot ${user.is_active ? 'success' : 'error'}`}>{user.is_active ? 'Aktif' : 'Pasif'}</span>
    <span>{date(user.created_at)}</span>
    <div className="user-actions"><Link className="user-link" to={`/users/${encodeURIComponent(user.id)}`}>İncele →</Link>
      <button className="secondary" disabled={user.is_admin || busy} onClick={toggle}>{busy ? 'Kaydediliyor…' : user.is_active ? 'Dondur' : 'Aç'}</button>
      {error && <small className="error" role="alert">{error}</small>}
    </div>
  </div>;
}

export function UserDetail() {
  const { userId } = useParams();
  return <UserRecord key={userId} userId={userId} />;
}

function UserRecord({ userId }) {
  const base = `/admin/users/${encodeURIComponent(userId)}`;
  const q = useResource(base);
  const [section, setSection] = useState('profile');
  return <>
    <Link className="user-link" to="/users">← Kullanıcılara dön</Link>
    <Result resource={q}>{({ user, counts, subscription }) => <>
      <header className="page-title"><div><h1>{user.display_name}</h1><p>{user.email}</p><small className="muted">{user.id}</small></div>
        <span className="status">{user.is_active ? 'Aktif hesap' : 'Pasif hesap'}</span></header>
      <p className="user-data-note">Hesaba kaydedilmiş veriler gösterilir. Cihazda henüz eşitlenmeyen bilgiler burada görünmez.</p>
      <div className="user-sections" aria-label="Kullanıcı bilgileri">
        {[
          ['profile', 'Profil'], ['memories', `Hafıza (${counts.memories})`],
          ['messages', `Sohbetler (${counts.messages})`], ['meals', `Öğünler (${counts.meals})`],
          ['exercises', `Egzersizler (${counts.exercises ?? '—'})`],
          ['habits', `Alışkanlıklar (${counts.habits ?? '—'})`], ['habit-feedback', 'Deneme sonuçları'],
        ].map(([id, title]) =>
          <button key={id} aria-pressed={section === id} className={section === id ? '' : 'secondary'} onClick={() => setSection(id)}>{title}</button>)}
      </div>
      {section === 'profile' ? <div className="user-profile">
        <PhysicalProfile preferences={user.preferences} />
        <section className="panel"><h2>Hesap bilgileri</h2><dl className="user-fields">
          {[
            ['Rol', user.is_admin ? 'Studio admin' : 'Üye'], ['Kayıt tarihi', date(user.created_at)],
            ['Son etkinlik', date(user.last_seen_at)], ['Son güncelleme', date(user.updated_at)],
            ['Saat dilimi', user.timezone], ['E-posta doğrulama', date(user.email_verified_at)],
          ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl></section>
        <ProfilePreferences preferences={user.preferences} />
        <section className="panel"><h2>Abonelik</h2>{subscription ? <dl className="user-fields">
          {[['Durum', ({ active: 'Aktif', trial: 'Deneme', expired: 'Sona ermiş', cancelled: 'İptal edilmiş' })[subscription.status] || subscription.status],
            ['Sağlayıcı', subscription.provider], ['Ürün', subscription.product_id || 'Belirtilmemiş'],
            ['Deneme bitişi', date(subscription.trial_ends_at)], ['Bitiş tarihi', date(subscription.expires_at)]].map(([label, value]) =>
              <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl> : <p>Hesaba kaydedilmiş abonelik yok.</p>}</section>
      </div> : <UserHistory key={section} base={base} section={section} userName={user.display_name} />}
    </>}</Result>
  </>;
}

function UserHistory({ base, section, userName }) {
  const [offset, setOffset] = useState(0);
  const q = useResource(`${base}/${section}?limit=${PAGE_SIZE}&offset=${offset}`);
  return <Result resource={q}>{({ items, total }) => <>
    <p className="user-data-note">En yeni kayıtlar önce gösterilir.</p>
    {!items.length && <p className="panel muted">{offset > 0 ? 'Bu sayfada artık kayıt yok. Önceki sayfaya dönebilirsin.' : ({
      memories: 'Hesaba kaydedilmiş hafıza bilgisi yok.', messages: 'Hesaba kaydedilmiş sohbet mesajı yok.',
      meals: 'Hesaba kaydedilmiş öğün yok.', exercises: 'Hesaba kaydedilmiş egzersiz yok.',
      habits: 'Henüz bir alışkanlık önerilmemiş.', 'habit-feedback': 'Kullanıcı henüz bir deneme sonucu paylaşmamış.',
    })[section]}</p>}
    <div className="user-history">{items.map(item => <article className={`panel user-entry ${section === 'messages' ? `message-${item.role}` : ''}`} key={item.id}>
      {section === 'memories' && <MemoryRecord item={item} />}
      {section === 'messages' && <><div className="row"><b>{item.role === 'user' ? userName : item.role === 'assistant' ? 'Nodi' : 'Sistem'}</b><time>{date(item.created_at)}</time></div>
        <p className="user-content">{item.content}</p>
        <small className="muted">Sohbet: {item.conversation_title} · {item.conversation_id}{item.model ? ` · ${item.provider || ''} / ${item.model}` : ''}</small></>}
      {section === 'meals' && <><div className="row"><h2>{item.title}</h2><time>{date(item.eaten_at)}</time></div>
        {item.note && <p className="user-content">{item.note}</p>}{Object.keys(item.analysis).length > 0 && <details><summary>Öğün ayrıntıları</summary><Fields value={item.analysis} /></details>}</>}
      {section === 'exercises' && <ExerciseRecord item={item} />}
      {section === 'habits' && <HabitRecord item={item} />}
      {section === 'habit-feedback' && <HabitFeedbackRecord item={item} />}
    </article>)}</div>
    <Pagination offset={offset} count={items.length} total={total} onChange={setOffset} />
  </>}</Result>;
}

function MemoryRecord({ item }) {
  return <>
    <div className="row"><h2>{memoryCategories[item.category] || labels[item.key] || 'Hatırlanan bilgi'}</h2>
      <span className={`record-state state-${item.status || 'confirmed'}`}>{memoryStates[item.status] || 'Kayıtlı'}</span></div>
    <p className="user-content">{item.text ?? item.value}</p>
    <div className="record-metadata">
      <span>Kaynak: {({ user: 'Kullanıcı', chat: 'Sohbet', pattern: 'Kayıtlardan gözlem' })[item.source] || 'Belirtilmemiş'}</span>
      <time>Güncelleme: {date(item.updated_at)}</time>
    </div>
    {Array.isArray(item.evidence) && item.evidence.length > 0 && <details>
      <summary>Bu bilgi neye dayanıyor?</summary>
      <ul className="memory-evidence">{item.evidence.map((text, index) => <li key={index}>{text}</li>)}</ul>
      {item.status === 'pending' && <p className="user-data-note">Kullanıcı henüz doğrulamadı; kesin bilgi olarak kullanılmaz.</p>}
    </details>}
    <small className="muted">İlk kayıt: {date(item.created_at)}</small>
  </>;
}

function ExerciseRecord({ item }) {
  return <>
    <div className="row"><h2>{item.kind}</h2><span className="record-state">{item.source === 'apple_health' ? 'Apple Sağlık' : 'Kullanıcı kaydı'}</span></div>
    <time>{date(item.started_at)}</time>
    <dl className="record-measures">
      <div><dt>Süre</dt><dd>{numberWithUnit(item.duration_minutes, 'dk')}</dd></div>
      <div><dt>Mesafe</dt><dd>{numberWithUnit(item.distance_km, 'km')}</dd></div>
      <div><dt>Kaydedilen enerji</dt><dd>{numberWithUnit(item.energy_kcal, 'kcal')}</dd></div>
    </dl>
    {item.note && <p className="user-content">{item.note}</p>}
  </>;
}

function HabitRecord({ item }) {
  return <>
    <div className="row"><h2>{item.title}</h2><span className={`record-state state-${item.status}`}>{habitStates[item.status] || item.status}</span></div>
    <p className="user-content">{item.action}</p>
    {item.reason && <div className="habit-reason"><b>Önerinin nedeni</b><p>{item.reason}</p></div>}
    <div className="record-metadata"><time>Önerildi: {date(item.created_at)}</time><time>Güncellendi: {date(item.updated_at)}</time></div>
  </>;
}

function HabitFeedbackRecord({ item }) {
  return <>
    <div className="row"><h2>{item.habit_title || 'Alışkanlık denemesi'}</h2><time>{date(item.created_at)}</time></div>
    <div className="record-metadata"><span className="record-state">{outcomes[item.outcome] || item.outcome}</span>
      <span>{helpfulnessLabels[item.helpfulness] || 'Yararlılık değerlendirmesi paylaşılmadı'}</span></div>
    {item.reason && <p className="user-content">{item.reason}</p>}
  </>;
}
